import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAllAccessGroups } from "../../api/AccessGroups";
import EntityMultiSelectForm from "../EntityMultiSelectForm";
import {
  getCustomerDisplayName,
  shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import { useAuth } from "../../providers/AuthContext";

const AccessGroupsTable = ({
  accessGroups = [],
  setAccessGroups,
  setHasChanges,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const includeOrganization = shouldShowOrganizationName(hasRole);
  const [availableAccessGroups, setAvailableAccessGroups] = useState([]);

  const selectedIds = useMemo(
    () =>
      accessGroups
        .filter((accessGroup) => !accessGroup.isDeleted)
        .map((accessGroup) => accessGroup.accessGroupPublicId)
        .filter(Boolean),
    [accessGroups]
  );

  const handleChange = (newSelectedIds) => {
    setHasChanges(true);

    setAccessGroups((prev) => {
      const availableById = new Map(
        availableAccessGroups.map((accessGroup) => [
          accessGroup.accessGroupPublicId,
          accessGroup,
        ])
      );

      const next = prev
        .map((accessGroup) => {
          const id = accessGroup.accessGroupPublicId;
          const shouldBeSelected = newSelectedIds.includes(id);

          if (shouldBeSelected) {
            return { ...accessGroup, isDeleted: false };
          }

          if (accessGroup.isNew) {
            return null;
          }

          return { ...accessGroup, isDeleted: true };
        })
        .filter(Boolean);

      newSelectedIds.forEach((id) => {
        const existing = next.find(
          (accessGroup) => accessGroup.accessGroupPublicId === id
        );

        if (!existing) {
          const accessGroup = availableById.get(id);

          if (accessGroup) {
            next.push({ ...accessGroup, isNew: true });
          }
        }
      });

      return next;
    });
  };

  return (
    <EntityMultiSelectForm
      title={t("entity.accessGroups")}
      fetchItems={async (token) => {
        const list = await getAllAccessGroups(token);

        return (list || []).map((accessGroup) => ({
          ...accessGroup,
          displayName: getCustomerDisplayName(
            { ...accessGroup, name: accessGroup.name || "" },
            includeOrganization
          ),
        }));
      }}
      labelKey="displayName"
      valueKey="accessGroupPublicId"
      selectedIds={selectedIds}
      onChange={handleChange}
      onItemsLoaded={setAvailableAccessGroups}
    />
  );
};

export default AccessGroupsTable;
